// PWA install state, shared app-wide.
//
// The browser's automatic install prompt is ALWAYS suppressed here
// (beforeinstallprompt + preventDefault) so desktop users never get an
// unsolicited popup. We surface install ourselves, on phones/tablets only:
// Android triggers the captured native prompt; iOS has no programmatic prompt
// at all (Safari limitation), so the UI shows Share → Add to Home Screen
// instructions instead.
//
// State lives at module level because beforeinstallprompt fires once, early —
// often before any component that cares about it has mounted.

import { ref, computed } from 'vue'

const deferredPrompt = ref(null)
const installed = ref(false)

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt.value = event
  })

  window.addEventListener('appinstalled', () => {
    installed.value = true
    deferredPrompt.value = null
  })

  // Already running as an installed app: display-mode covers Android/desktop,
  // navigator.standalone is the legacy iOS Safari flag.
  if (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  ) {
    installed.value = true
  }
}

function isMobileDevice() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true
  // iPadOS 13+ reports a Macintosh user agent; touch points give it away.
  return /iPad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export function usePwaInstall() {
  const isIos = computed(() => {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent || ''
    return (
      /iPad|iPhone|iPod/i.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    )
  })

  // A native prompt is available (Android Chrome, desktop Chrome). iOS never
  // fires beforeinstallprompt, so this stays false there.
  const canPrompt = computed(() => deferredPrompt.value !== null && !installed.value)

  // Whether to show our install UI at all: phones/tablets only, never when
  // already installed. iOS always qualifies (instructions); other platforms
  // only once the browser has offered us the native prompt.
  const showInstallUi = computed(
    () => !installed.value && isMobileDevice() && (canPrompt.value || isIos.value)
  )

  // Trigger the captured native prompt. Returns 'accepted' | 'dismissed' |
  // 'unavailable' (caller should show iOS instructions for 'unavailable').
  async function promptInstall() {
    if (!deferredPrompt.value) return 'unavailable'
    const promptEvent = deferredPrompt.value
    deferredPrompt.value = null
    promptEvent.prompt()
    const { outcome } = await promptEvent.userChoice
    if (outcome === 'accepted') installed.value = true
    return outcome
  }

  return { showInstallUi, canPrompt, isIos, installed, promptInstall }
}
